import { DeleteOutlineRounded, DoneRounded, NotificationsRounded } from "@mui/icons-material";
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { MouseEvent, useEffect, useState } from "react";

import { useTranslation } from "@/context/I18nProvider";
import { getSocket } from "@/lib/socket";
import {
  getCrmUnreadCount,
  getMyCrmNotifications,
  markAllCrmRead,
  markCrmViewed,
  removeCrmNotification,
} from "@/services";
import { useSelector } from "@/store";
import { CrmNotification } from "@/types";
import { handleError } from "@/utils";

// Header bell: lists the signed-in admin's CRM notifications and updates live.
// New rows + unread count arrive over the shared socket (room = userId); the
// backend emits `newNotification` / `countNotification` from sendCrmNotification.
const NotificationMenu = () => {
  const { t } = useTranslation();
  const token = useSelector((state) => state.auth.token);
  const userId = useSelector((state) => state.auth.user?._id);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<CrmNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  // Ids whose body is expanded (full text); collapsed rows clamp to 2 lines.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const open = !!anchorEl;

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [list, count] = await Promise.all([getMyCrmNotifications(), getCrmUnreadCount()]);
        if (cancelled) return;
        setNotifications(list);
        setUnreadCount(count.count);
      } catch (error) {
        handleError(error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!token || !userId) return;
    const socket = getSocket(token);
    if (!socket) return;

    const handleNew = (incoming: CrmNotification) => {
      setNotifications((prev) => [incoming, ...prev]);
      setUnreadCount((prev) => prev + 1);
    };
    const handleCount = (count: number) => {
      setUnreadCount(count);
    };

    socket.on("newNotification", handleNew);
    socket.on("countNotification", handleCount);

    return () => {
      socket.off("newNotification", handleNew);
      socket.off("countNotification", handleCount);
    };
  }, [token, userId]);

  const handleOpen = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleMarkAll = async () => {
    try {
      await markAllCrmRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, viewed: true })));
      setUnreadCount(0);
    } catch (error) {
      handleError(error);
    }
  };

  // Mark a single notification read. Keeps the menu open (stopPropagation).
  const handleMarkOne = async (event: MouseEvent<HTMLElement>, id: string) => {
    event.stopPropagation();
    try {
      await markCrmViewed(id);
      setNotifications((prev) => {
        const target = prev.find((n) => n._id === id);
        if (target && !target.viewed) setUnreadCount((count) => Math.max(0, count - 1));
        return prev.map((n) => (n._id === id ? { ...n, viewed: true } : n));
      });
    } catch (error) {
      handleError(error);
    }
  };

  // Soft-delete a notification (sets `deleted` server-side).
  const handleDelete = async (event: MouseEvent<HTMLElement>, id: string) => {
    event.stopPropagation();
    try {
      await removeCrmNotification(id);
      setNotifications((prev) => {
        const target = prev.find((n) => n._id === id);
        if (target && !target.viewed) setUnreadCount((count) => Math.max(0, count - 1));
        return prev.filter((n) => n._id !== id);
      });
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <>
      <Badge badgeContent={unreadCount} color="error" overlap="circular" max={99}>
        <IconButton
          size="small"
          onClick={handleOpen}
          aria-label={t("common.openNotifications")}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
        >
          <NotificationsRounded />
        </IconButton>
      </Badge>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        slotProps={{ paper: { sx: { width: 360, maxHeight: 420, mt: 1 } } }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.5 }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t("crm.notificationTitle")}
          </Typography>
          <Button size="small" onClick={handleMarkAll} disabled={unreadCount === 0}>
            {t("notification.mark")}
          </Button>
        </Stack>
        <Divider />
        {notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {t("notification.empty")}
            </Typography>
          </Box>
        ) : (
          notifications.map((item) => {
            const expanded = expandedIds.has(item._id);
            return (
              <MenuItem
                key={item._id}
                disableRipple
                onClick={() => toggleExpanded(item._id)}
                sx={{
                  whiteSpace: "normal",
                  alignItems: "flex-start",
                  gap: 1,
                  py: 1.25,
                  bgcolor: item.viewed ? "transparent" : "action.hover",
                }}
              >
                <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0, opacity: item.viewed ? 0.7 : 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {!item.viewed && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "primary.main",
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: item.viewed ? 500 : 700, lineHeight: 1.3 }}
                    >
                      {item.title}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      wordBreak: "break-word",
                      ...(expanded
                        ? { whiteSpace: "pre-wrap" }
                        : {
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }),
                    }}
                  >
                    {item.message}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {new Date(item.createdAt).toLocaleString()}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                  {!item.viewed && (
                    <Tooltip title={t("notification.mark")}>
                      <IconButton
                        size="small"
                        aria-label="Mark as read"
                        onClick={(event) => handleMarkOne(event, item._id)}
                      >
                        <DoneRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={t("common.delete")}>
                    <IconButton
                      size="small"
                      aria-label="Delete notification"
                      onClick={(event) => handleDelete(event, item._id)}
                    >
                      <DeleteOutlineRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </MenuItem>
            );
          })
        )}
      </Menu>
    </>
  );
};

export default NotificationMenu;
